import { buildApi, createFetchAndCheckResponse } from '../http-client/http-client';
import { hasValue, toQueryParams } from '../../utils/common-utils';
import AuthData from './auth-data';
import { TsFixMeType } from '../../types';

const authUrl = `${process.env.OAUTH_BASE_URL}/oauth/v2/token`;
const oauthDescribeTokenUrl = `${process.env.OAUTH_BASE_URL}/oauth/v2/describe-token-info?grant_type=authorization_code`;
const oauthTokenUrl = `${authUrl}?grant_type=authorization_code`;

const publicUsername = process.env.USERNAME_APP as string;
const publicPassword = process.env.PASSWORD_APP as string;
const publicClientId = process.env.CLIENT_ID_APP as string;
const publicSecret = process.env.SECRET_APP as string;

const publicApi = buildApi();

interface authenticateProps {
  username: string;
  password: string;
  persist: boolean;
}
export const authenticate = ({ username, password, persist }: authenticateProps) => {
  const body = [
    `${encodeURIComponent('grant_type')}=${encodeURIComponent('password')}`,
    `${encodeURIComponent('username')}=${encodeURIComponent(username)}`,
    `${encodeURIComponent('password')}=${encodeURIComponent(password)}`,
  ];
  return publicApi
    .post(authUrl, body.join('&'), false, { 'Content-Type': 'application/x-www-form-urlencoded' })
    .then(data => {
      const authData = { username, token: data.token, refresh_token: data.refresh_token };
      return AuthData.set(authData, persist).then(() => authData);
    });
};

export const getPublicToken = () => {
  const body = [
    `${encodeURIComponent('grant_type')}=${encodeURIComponent('password')}`,
    `${encodeURIComponent('username')}=${encodeURIComponent(publicUsername)}`,
    `${encodeURIComponent('password')}=${encodeURIComponent(publicPassword)}`,
    `${encodeURIComponent('client_id')}=${encodeURIComponent(publicClientId)}`,
    `${encodeURIComponent('client_secret')}=${encodeURIComponent(publicSecret)}`,
  ];
  return publicApi
    .post(authUrl, body.join('&'), false, { 'Content-Type': 'application/x-www-form-urlencoded' })
    .then(authData => authData.token);
};

export const getToken = async () => {
  const data = await AuthData.get();
  if (hasValue(data)) {
    return data.token;
  }
  return getPublicToken();
};

const authRefreshTokenUrl = (refreshToken: string) =>
  `${process.env.OAUTH_BASE_URL}/oauth/v2/token?grant_type=refresh_token&refresh_token=${refreshToken}`;

export const refreshTokenRequest = (refreshToken: string) => {
  const body = {};
  const headers = {};
  return createFetchAndCheckResponse(authRefreshTokenUrl(refreshToken), {
    body,
    headers,
    method: 'POST',
    cache: 'default',
  });
};

export const authenticateWithRefreshToken = (refreshToken: string) => {
  return refreshTokenRequest(refreshToken).then(data => {
    const authData = { ...data };
    return AuthData.set(authData).then(() => {
      sessionResetHandler();
      return authData;
    });
  });
};

interface registerProps {
  email: string;
  password: string;
  confirmPassword: string;
  source: string;
  form: TsFixMeType;
  additionalHeaders: { (key: string): TsFixMeType } | {};
}
export const register = ({ email, password, confirmPassword, source, form, additionalHeaders = {} }: registerProps) => {
  const formData = {
    'user_registration[email]': email,
    'user_registration[plainPassword][first]': password,
    'user_registration[plainPassword][second]': confirmPassword,
    'user_registration[username]': email,
    'user_registration[additionalUserInfo][source]': source,
    'user_registration[additionalUserInfo][form]': form,
  };

  return getPublicToken().then(token =>
    buildApi(token).postFormData(`${process.env.OAUTH_BASE_URL}/oauth/api/register/`, formData, additionalHeaders),
  );
};

interface socialAuthProps {
  socialToken: string;
  clientType: string;
  source: string;
  form: TsFixMeType;
}
export const socialAuth = async ({ socialToken, clientType, source, form }: socialAuthProps) => {
  const describeTokenUrl = `${oauthDescribeTokenUrl}&additionalUserInfo[source]=${source}&additionalUserInfo[form]=${form}&client_id=${clientType}&code=${socialToken}`;
  const { email, registered } = await publicApi.get(describeTokenUrl);

  if (!hasValue(email)) {
    throw Error('Email not found in token');
  }
  const tokenUrl = `${oauthTokenUrl}&additionalUserInfo[source]=${source}&additionalUserInfo[form]=${form}&client_id=${clientType}&code=${socialToken}`;
  const { token, refresh_token } = await publicApi.get(tokenUrl);
  const authData = {
    username: email,
    token,
    refresh_token,
  };
  return AuthData.set(authData).then(() => ({ authData, userExists: registered }));
};

export const signOut = async () => {
  AuthData.remove().then(() => sessionEndHandler());
};

let sessionEndHandler = () => {};

export const onSessionEnd = (callback: () => void) => {
  sessionEndHandler = callback;
  return () => {
    sessionEndHandler = () => {};
  };
};

export const resetPassword = (email: string) => {
  const formData = {
    email,
  };
  return getPublicToken().then(token =>
    buildApi(token).postFormData(`${process.env.OAUTH_BASE_URL}/oauth/api/resetting/send-email`, formData),
  );
};

export const autoLoginSwitchAccount = (params: { _switch_user: string }) => {
  return getToken().then(token =>
    buildApi(token)
      .get(`${process.env.OAUTH_BASE_URL}/oauth/api/auto_login?${toQueryParams(params)}`)
      .then(authData => {
        authData['token'] = authData['access_token'];
        delete authData['access_token'];
        return AuthData.set(authData).then(() => sessionResetHandler());
      }),
  );
};

let sessionResetHandler = () => {};

export const onSessionReset = (callback: () => void) => {
  sessionResetHandler = callback;
  return () => {
    sessionResetHandler = () => {};
  };
};

export const switchAccount = (email: string) => {
  return autoLoginSwitchAccount({
    _switch_user: email,
  });
};

export default {
  authenticate,
  authenticateWithRefreshToken,
  signOut,
  getPublicToken,
  refreshTokenRequest,
  register,
  socialAuth,
  resetPassword,
  switchAccount,
};
